import { Switch as HSwitch } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../cn";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  optional?: boolean;
  /** Text shown on the right of the label (e.g. "Format: CPMDxxx"). */
  aside?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, optional, aside, htmlFor, className, children }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {(label || aside) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <label htmlFor={htmlFor} className="text-label-md text-on-surface">
              {label}
              {required && <span className="ml-0.5 text-error">*</span>}
              {optional && <span className="ml-1.5 font-normal text-on-surface-variant">(optional)</span>}
            </label>
          )}
          {aside && <span className="text-label-sm text-on-surface-variant">{aside}</span>}
        </div>
      )}
      {children}
      {error ? (
        <p role="alert" className="flex items-center gap-1 text-body-sm font-medium text-error">
          {error}
        </p>
      ) : (
        hint && <p className="text-body-sm text-on-surface-variant">{hint}</p>
      )}
    </div>
  );
}

const control =
  "w-full rounded-control bg-surface-container-low text-on-surface ring-1 ring-inset ring-transparent transition-[background-color,box-shadow] duration-150 placeholder:text-outline focus:bg-surface-container-lowest focus:outline-none focus:ring-2 focus:ring-primary-container/70 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:bg-error-container/25 aria-[invalid=true]:ring-error/60 text-base md:text-body-md";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Layout classes for the outer wrapper (grid spans, widths). `className` styles the control itself. */
  wrapperClassName?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leading, trailing, invalid, className, wrapperClassName, ...props },
  ref,
) {
  return (
    <div className={cn("relative flex items-center", wrapperClassName)}>
      {leading && (
        <span className="pointer-events-none absolute left-3.5 flex text-on-surface-variant [&_svg]:size-[18px]">{leading}</span>
      )}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(control, "h-11", leading ? "pl-10" : "pl-3.5", trailing ? "pr-11" : "pr-3.5", className)}
        {...props}
      />
      {trailing && <span className="absolute right-1.5 flex">{trailing}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid, className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(control, "min-h-24 resize-y px-3.5 py-2.5 leading-relaxed", className)}
      {...props}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Layout classes for the outer wrapper (grid spans, widths). `className` styles the control itself. */
  wrapperClassName?: string;
  invalid?: boolean;
  leading?: ReactNode;
}

/** Native select: best keyboard + mobile picker support. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, leading, className, wrapperClassName, children, ...props },
  ref,
) {
  return (
    <div className={cn("relative flex items-center", wrapperClassName)}>
      {leading && (
        <span className="pointer-events-none absolute left-3.5 flex text-on-surface-variant [&_svg]:size-[18px]">{leading}</span>
      )}
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(control, "h-11 appearance-none pr-10", leading ? "pl-10" : "pl-3.5", className)}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 size-[18px] text-on-surface-variant" aria-hidden />
    </div>
  );
});

export interface SwitchProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({ checked, onChange, label, disabled, className }: SwitchProps) {
  return (
    <HSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "group relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-surface-container-highest transition-colors duration-200 data-checked:bg-primary-container data-disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-container",
        className,
      )}
    >
      <span className="size-5 translate-x-0.5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out-soft group-data-checked:translate-x-[22px]" />
    </HSwitch>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, className, id, ...props }: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <label htmlFor={inputId} className={cn("flex cursor-pointer items-start gap-2.5 select-none", className)}>
      <input
        id={inputId}
        type="checkbox"
        className="mt-0.5 size-[18px] shrink-0 cursor-pointer rounded accent-primary-container"
        {...props}
      />
      <span className="flex flex-col">
        <span className="text-body-md text-on-surface">{label}</span>
        {description && <span className="text-body-sm text-on-surface-variant">{description}</span>}
      </span>
    </label>
  );
}
